import * as _ from 'underscore';
import { ResultHelper } from '../helpers/ResultHelper';
import { L10n } from '../L10n/L10n';
import { IErrorItem, MultipleItemResult } from '../models/MultipleItemResult';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IResult } from '../ServerApi/types/IResponse';
import {
  ContentActionNotification,
  IErrorDetail,
  IMultipleResultNotificationOptions,
  INotificationTranslationIds,
  MultipleResultNotificationOptions,
} from './ContentActionNotification';

export type ActionName = 'change-owner' | 'move';

export class ActionHelpers {

  public static $inject = ['$translate', 'ContentActionNotification'];

  private static DefaultItemNameFn = (item: any) => item.name;

  constructor(
    private $translate: ng.translate.ITranslateService,
    private ContentActionNotification: ContentActionNotification
  ) { }

  public standardErrorDetails<T>(
    result: MultipleItemResult<T>,
    urlFn: (errorItem: T) => string,
    errorMessageFn: (errorItem: IErrorItem<T>) => string,
    itemNameFn: (item: T) => string = ActionHelpers.DefaultItemNameFn
  ) {

    // Default item name function just uses 'item.name'
    itemNameFn = itemNameFn || ((item: any) => item.name);

    return _.map(result.errorItems(), (errorItem: IErrorItem<T>) => {
      return {
        itemName: itemNameFn(errorItem.item),
        itemUrl: urlFn(errorItem.item),
        errorMessage: errorMessageFn(errorItem)
      };
    });

  }

  public standardErrorNotification<T>(
    result: MultipleItemResult<T>,
    translations: INotificationTranslationIds,
    describeErrorDetail: (errorItem: IErrorItem<T>) => IErrorDetail,
    itemNameFn: (item: T) => string = ActionHelpers.DefaultItemNameFn,
    extraTranslations?: object
  ): IMultipleResultNotificationOptions {

    const firstErrorItem = result.anyFailed() ? result.errorItems()[0] : undefined;
    const firstErrorItemName = firstErrorItem && firstErrorItem.item ? itemNameFn(firstErrorItem.item) : '';

    return MultipleResultNotificationOptions.create(result,
      {
        describeSuccessSummary: (successCount: number) =>
          this.$translate.instant(translations.successId, {
            ITEM_COUNT: successCount,
            ITEM_NAME: L10n.elideIfLong(itemNameFn(result.firstSuccessfulItem())),
            ...extraTranslations
          }, L10n.Interpolations.Count),
        describeErrorSummary: (errorCount: number) =>
          this.$translate.instant(translations.errorId, {
            ERROR_COUNT: errorCount,
            ITEM_NAME: L10n.elideIfLong(firstErrorItemName)
          }, L10n.Interpolations.Count),
        describeErrorDetail: describeErrorDetail
      }
    );
  }

  public notifyStandardError<T>(
    items: Set<T>,
    translations: INotificationTranslationIds,
    describeErrorDetail: (errorItem: IErrorItem<T>) => IErrorDetail,
    extraTranslations?: object
  ) {
    return (result: IResult) => {
      if (!_.isUndefined(result) && !_.isUndefined(result.errors) &&
        result.errors.length > 0 && _.isUndefined(result.errors[0].id)) {
        this.ContentActionNotification.notifyGenericError(result.errors[0]);
        return result;
      } else {
        const itemResult = new MultipleItemResult(items, result);
        const errorNotificationOptions = this.standardErrorNotification(itemResult, translations, describeErrorDetail, ActionHelpers.DefaultItemNameFn, extraTranslations);
        this.ContentActionNotification.notify(errorNotificationOptions);
        return result; // to allow promise chaining
      }
    };
  }

  // static

  // When performing most actions, keep the selection except remove any items that were not found.
  // This helps to avoid "ghost" items in your selection - items that are still in your selection but are not in the list.
  public static deselectNotFound<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsIn(ResultHelper.getErrorIdsWithCode(result, notFoundCode));
  }

  // When performing delete-like actions, clear the selection, except keep any items that encountered an error other than "not found".
  // This allows you to see the items that failed and try again or perform some other action.
  public static deselectAllExceptErrors<T>(selectedItems: Set<T>, result: IResult, notFoundCode: number) {
    selectedItems.removeItemsNotIn(ResultHelper.getErrorIds(result, { excludeCode: notFoundCode }));
  }
}

Module.instance().service('ActionHelpers', ActionHelpers);
